// Side-effect import: synchronously initializes i18n before this module's
// top-level $t calls run (ES module evaluation order guarantees it).
import { apiQueryQuoteStatusTotalCount } from '@/cms/api/quoteApi';
import { TableFilterCardItemEditTypeEnum } from '@/cms/components/CardList/tableFilterCardList/item';
import '@/cms/i18n';
import { getQuoteListCardData } from '@/cms/util/storage';
import {
  FilterRegionList,
  InsuranceType,
  PartnerType,
  QUOTE_PURCHASE_PROBABILITY_LIST,
  QuoteBillPaymentStatusEnum,
  QuoteCountTagEnum,
  QuoteCountTagStatusList,
  QuoteRenewStatus,
  QuoteStatus,
  QuoteTagEnum,
  QuoteTagStatusList,
  apiGetAllOrganizations,
  apiGetAllQuotes,
  apiQueryTripartiteCooperationList,
  fetchFilterUsers,
  getBrokerQuotingThroughMap,
  getLocalTranslation,
  getPaymentScheduleStatusList,
  getQuoteCreationMethodList,
  getQuoteRenewStatusMap,
  getRealDisplayProviderSourceList,
  getRealDisplayQuoteStatusList,
  getReferralStatusList,
  getValidationStatusList,
  truelyValueMap,
} from '@jove-frontend/uikit';
import axios from 'axios';
import { cloneDeep } from 'lodash';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { createCatalogStoreFactory } from '../catalog/CatalogStore';
import { IQuoteCatalogStore } from './types';

const { $t } = getLocalTranslation();

export const useQuotesCatalog = createCatalogStoreFactory<IQuoteCatalogStore>();

export const useQuotesCatalogPageStore = create<IQuoteCatalogStore>()(
  immer((set, get) => ({
    state: {
      quotes: null,
      catalogColumns: [
        { label: $t('common:id'), labelKey: 'common:id', name: 'id', isSortable: false },
        {
          label: $t('page:quoteListTableColumnFilter.quote_creation_methods'),
          labelKey: 'page:quoteListTableColumnFilter.quote_creation_methods',
          name: 'quote_creation_method',
          isSortable: false,
        },
        {
          label: $t('page:quoteListTableColumnFilter.source'),
          labelKey: 'page:quoteListTableColumnFilter.source',
          name: 'source',
          isSortable: false,
        },
        { label: $t('common:created'), labelKey: 'common:created', name: 'created_at', isSortable: true },
        { label: $t('common:updated'), labelKey: 'common:updated', name: 'updated_at', isSortable: true },
        {
          label: $t('page:quoteListTableColumnFilter.purchased_at'),
          labelKey: 'page:quoteListTableColumnFilter.purchased_at',
          name: 'purchased_at',
          isSortable: true,
        },
        {
          label: $t('common:company_name'),
          labelKey: 'common:company_name',
          name: 'companyName',
          isSortable: false,
        },
        {
          label: $t('common:customer_name'),
          labelKey: 'common:customer_name',
          name: 'firstName',
          isSortable: false,
        },
        { label: $t('common:email'), labelKey: 'common:email', name: 'email', isSortable: false },
        { label: $t('common:phone'), labelKey: 'common:phone', name: 'phone', isSortable: false },
        {
          label: $t('common:insurance_type'),
          labelKey: 'common:insurance_type',
          name: 'insuranceType',
          isSortable: false,
        },
        { label: $t('common:assignee'), labelKey: 'common:assignee', name: 'assigneeId', isSortable: false },
        {
          label: $t('page:quoteListTableColumnFilter.referral_assignee'),
          labelKey: 'page:quoteListTableColumnFilter.referral_assignee',
          name: 'referral_assignee_id',
          isSortable: false,
        },
        {
          label: $t('common:insurer'),
          labelKey: 'common:insurer',
          name: 'providerCode',
          isSortable: false,
        },
        {
          label: $t('common:purchase_probability'),
          labelKey: 'common:purchase_probability',
          name: 'purchaseProbability',
          isSortable: false,
        },
        {
          label: $t('common:quote_number'),
          labelKey: 'common:quote_number',
          name: 'quoteNumber',
          isSortable: false,
        },
        {
          label: $t('common:policy_number'),
          labelKey: 'common:policy_number',
          name: 'policyNumber',
          isSortable: false,
        },
        {
          label: $t('pages:brokers.broker_name'),
          labelKey: 'pages:brokers.broker_name',
          name: 'brokerName',
          isSortable: false,
        },
        {
          label: $t('pages:partners.partner_name'),
          labelKey: 'pages:partners.partner_name',
          name: 'partnerName',
          isSortable: false,
        },
        {
          label: $t('pages:partners.partner_referral_fee'),
          labelKey: 'pages:partners.partner_referral_fee',
          name: 'partnerReferralFee',
          isSortable: false,
        },
        {
          label: $t('pages:quotes.partner_fee'),
          labelKey: 'pages:quotes.partner_fee',
          name: 'partnerFee',
          isSortable: false,
        },
        {
          label: $t('common:region'),
          labelKey: 'common:region',
          name: 'region',
          isSortable: false,
        },
        { label: $t('common:contract_start_date'), labelKey: 'common:contract_start_date', name: 'contractStartDate', isSortable: true },
        { label: 'Property analysis not available', name: 'property_analysis_not_available', isSortable: false },
        { label: 'Sanction check referral', name: 'sanction_check_referral', isSortable: false },
        {
          label: $t('common:aggregate_price'),
          labelKey: 'common:aggregate_price',
          name: 'aggregate_price',
          isSortable: true,
        },
        {
          label: $t('common:renewal_status'),
          labelKey: 'common:renewal_status',
          name: 'renewalStatus',
          isSortable: false,
        },
        {
          label: $t('common:organization_name'),
          labelKey: 'common:organization_name',
          name: 'organizationName',
          isSortable: false,
        },
        {
          label: $t('common:email_open_rate'),
          labelKey: 'common:email_open_rate',
          name: 'emailOpenRate',
          isSortable: true,
        },
        {
          label: $t('common:email_click_rate'),
          labelKey: 'common:email_click_rate',
          name: 'emailClickRate',
          isSortable: true,
        },
        { label: $t('kit:policy_start_date'), labelKey: 'kit:policy_start_date', name: 'policy_start_date', isSortable: false },
        {
          label: $t('page:quoteListTableColumnFilter.policy_document_expiry_date'),
          labelKey: 'page:quoteListTableColumnFilter.policy_document_expiry_date',
          name: 'policy_expiry_date',
          isSortable: true,
        },
        {
          label: $t('page:quoteListTableColumnFilter.policy_document_expiration_days'),
          labelKey: 'page:quoteListTableColumnFilter.policy_document_expiration_days',
          name: 'policyExpirationDays',
          isSortable: false,
        },
        {
          label: $t('page:quoteListTableColumnFilter.validation_status'),
          labelKey: 'page:quoteListTableColumnFilter.validation_status',
          name: 'validation_status',
          isSortable: false,
        },
        { label: $t('common:status'), labelKey: 'common:status', name: 'status', isSortable: false },
        {
          label: $t('page:quoteListTableColumnFilter.duplicate_quote'),
          labelKey: 'page:quoteListTableColumnFilter.duplicate_quote',
          name: 'duplicateQuote',
          isSortable: false,
        },
        { label: $t('common:copy_URL'), labelKey: 'common:copy_URL', name: 'copyURL', isSortable: false },
      ],
      filterModel: {
        search: null,
        status: getRealDisplayQuoteStatusList()
          .filter((item) => ![QuoteStatus.OBTAINED, QuoteStatus.PARTIALLY_OBTAINED].includes(item.value))
          .map((item) => {
            return {
              ...item,
              checked: false,
            };
          }),
        validationStatusList: getValidationStatusList().map((item) => {
          return {
            ...item,
            checked: false,
          };
        }),
        insuranceType: [
          {
            value: InsuranceType.BUSINESS_INSURANCE,
            label: $t('common:business_insurance'),
            checked: false,
          },
          {
            value: InsuranceType.CONSTRUCTION_INSURANCE,
            label: $t('common:construction'),
            checked: false,
          },
          {
            value: InsuranceType.GOODS_IN_TRANSIT_INSURANCE,
            label: $t('common:goods_in_transit'),
            checked: false,
          },
        ],
        assignee: [],
        referralAssigneeList: [],
        user_country: null,
        business_country: null,
        phone_country: null,
        purchaseProbability: QUOTE_PURCHASE_PROBABILITY_LIST().map((item) => {
          return { ...item, checked: false };
        }),
        createTimeRange: null,
        tags: QuoteTagStatusList()
          .filter((item) => ![QuoteTagEnum().docSent.value, QuoteTagEnum().partiallyQuoted.value].includes(item.value))
          .map((item) => {
            return { ...item, checked: false };
          }),
        countTags: QuoteCountTagStatusList().map((item) => {
          return {
            ...item,
            checked: false,
            filterValue: 0,
          };
        }),
        regions: FilterRegionList().map((item) => {
          return { ...item, checked: false };
        }),
        paymentTimeRange: null,
        purchaseTimeRange: null,
        minimumPrice: null,
        maximumPrice: null,
        renewStatusList: [
          {
            value: QuoteRenewStatus.RENEW_SOON,
            label: $t('pages:quotes.renew_soon'),
            checked: false,
          },
        ],
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        organizations: [],
        insurers: getRealDisplayProviderSourceList().map((item) => ({
          ...item,
          checked: false,
          label: item.displayName,
        })),
        partners: [],
        quotingThrough: [
          getBrokerQuotingThroughMap().landingPage,
          { ...getBrokerQuotingThroughMap().brokerPortal, label: 'CMS' },
          getBrokerQuotingThroughMap().validation,
        ].map((item) => {
          return {
            ...item,
            checked: false,
          };
        }),
        removeDuplicatedList: [
          {
            value: truelyValueMap.yes.value,
            label: $t('page:quoteCatalogPageStore.filter_out_duplicated_quote_data'),
            checked: false,
          },
        ],
        propertyAnalysisNotAvailable: [
          {
            value: truelyValueMap.yes.value,
            label: 'Property analysis not available',
            checked: false,
          },
        ],
        sanctionCheckReferral: [
          {
            value: truelyValueMap.yes.value,
            label: 'Sanction check referral',
            checked: false,
          },
        ],
        quoteCreationMethodList: getQuoteCreationMethodList(),
        paymentStatus: getPaymentScheduleStatusList()
          .filter((item) => item.value !== QuoteBillPaymentStatusEnum.SUCCEEDED)
          .map((item) => {
            return { ...item, checked: false };
          }),
        referralStatus: getReferralStatusList().map((item) => {
          return { ...item, checked: false };
        }),
        referralTimeRange: null,
        minimumDaysPolicyToExpiry: null,
      },
      statusCountList: [],
      statusCountInterfaceCancelToken: null,
    },
    actions: {
      initializeFilters: async () => {
        const users = await fetchFilterUsers({ organization_slug: 'joveco' });
        const [organizaionRes] = await apiGetAllOrganizations({
          offset: 0,
          limit: 1000,
        });

        const [partnerRes] = await apiQueryTripartiteCooperationList({ limit: 100, offset: 0, sourceType: PartnerType.GENERAL });

        set((store) => {
          store.state.filterModel.assignee = users;
          store.state.filterModel.referralAssigneeList = cloneDeep(users);
          store.state.filterModel.organizations = (organizaionRes?.data || []).map((item) => {
            return {
              ...item.organization,
              value: item.organization?.id,
              label: item.organization?.name,
              checked: false,
            };
          });
          store.state.filterModel.partners = (partnerRes?.data || []).map((item) => {
            return {
              value: item.id,
              label: item.name,
              checked: false,
            };
          });
        });

        return get().state.filterModel;
      },
      fetchQuotes: async (args) => {
        const [res, err] = await apiGetAllQuotes(...args);

        get().actions.queryQuoteCountDataList();

        return res;
      },
      // Quotes - status filter
      queryQuoteCountDataList: async () => {
        const originList = cloneDeep(get().state.statusCountList);
        const cancelToken = get().state.statusCountInterfaceCancelToken;

        // Cached count input data
        const countParams = {
          quote_viewed: 0,
          email_viewed: 0,
        };
        const saveData = getQuoteListCardData();
        originList.forEach((item) => {
          if (item.isSelected && item.value === QuoteCountTagEnum().quoteViewed.value) {
            countParams.quote_viewed = Number(item.filterValue || 1);
          } else if (item.isSelected && item.value === QuoteCountTagEnum().emailViewed.value) {
            countParams.email_viewed = Number(item.filterValue || 1);
          }
        });

        // The interface take too long.
        cancelToken?.cancel();
        const newCancelToken = axios.CancelToken.source();
        set((store) => {
          store.state.statusCountInterfaceCancelToken = newCancelToken;
        });
        const [res] = await apiQueryQuoteStatusTotalCount(countParams, {
          cancelToken: newCancelToken.token,
        });

        if (res) {
          if (originList.length === 0) {
            const list = [];
            const statusList = get().state.filterModel.status;
            const tags = get().state.filterModel.tags;
            const renewStatusList = get().state.filterModel.renewStatusList;

            // Failed, Tags
            [
              QuoteTagEnum().switchToJove.value,
              QuoteTagEnum().docNotSent.value,
              QuoteTagEnum().referralApproved.value,
              QuoteTagEnum().purchasedWithoutDOTNumber.value,
              QuoteStatus.PURCHASE_FAILED,
              QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED,
              QuoteStatus.CANCELLED,
              QuoteCountTagEnum().quoteViewed.value,
              QuoteCountTagEnum().emailViewed.value,
              QuoteTagEnum().renewalPaymentFailed.value,
              getQuoteRenewStatusMap().renewSoon.value,
            ].forEach((item) => {
              const temp = res.data.find((aItem) => aItem.status === item);

              const findItem = [...statusList, ...QuoteCountTagStatusList(), ...tags, ...renewStatusList].find((aItem) => aItem.value === item);

              if (findItem) {
                const listItem = {
                  key: item,
                  value: findItem.value,
                  title: findItem.label,
                  content: temp?.count || 0,
                  isSelected: false,
                  canEdit: false,
                  editType: TableFilterCardItemEditTypeEnum.Normal,
                  filterValue: '',
                };
                if ([QuoteCountTagEnum().quoteViewed.value, QuoteCountTagEnum().emailViewed.value].includes(findItem?.value as any)) {
                  listItem.canEdit = true;
                  listItem.editType = TableFilterCardItemEditTypeEnum.Count;
                  const temp = (saveData?.countTagList || []).find((aItem) => aItem.value === findItem.value);
                  listItem.filterValue = temp?.filterValue || 1;
                }
                if (listItem) list.push(listItem);
              }
            });

            set((store) => {
              store.state.statusCountList = list;
            });
          } else {
            // Update count
            originList.forEach((item) => {
              const temp = res.data.find((aItem) => aItem.status === item.key);
              if (temp) {
                item.content = temp.count;
              }
            });
            set((store) => {
              store.state.statusCountList = originList;
            });
          }
        }
      },
      // Update item info
      updateQuoteCountStatusItem: (list) => {
        set((store) => {
          store.state.statusCountList = cloneDeep(list);
        });
      },
      // Clean status
      cleanQuoteStatusCountInitialInfo: () => {
        // Temporary
        set((store) => {
          const list = cloneDeep(get().state.statusCountList);
          const statusList = cloneDeep(get().state.filterModel.status);
          const countTaglist = cloneDeep(get().state.filterModel.countTags);
          list.forEach((item) => (item.isSelected = false));
          statusList.forEach((item) => (item['checked'] = false));
          countTaglist.forEach((item) => (item['checked'] = false));
          store.state.statusCountList = list;
          store.state.filterModel.status = statusList;
          store.state.filterModel.countTags = countTaglist;
        });
      },
    },
  }))
);
